// The words of the Admin page's agent permissions dashboard, and the helpers that read its
// settings (docs/superpowers/specs/2026-10-15-agent-permissions-design.md, "Settings"):
//
//   {"areas": {"charts": "full|read|off", "fax": ..., "tasks": ..., "outreach": ...},
//    "sends": {"sms": "allowed|ask|off", "email": ..., "fax": ...},
//    "auto": true}
//
// A clinic's defaults set every key. A person's override sets only the keys they don't follow
// the clinic default for. Settings only narrow: an agent never goes beyond its user's own access.
//
// Every area is Off / Read-only / Full (the user's decision). Charts has no change actions today
// (backend api/agents/types.py WRITE_AREAS), so its row carries a note that Read-only and Full do
// the same there.
//
// What this page doesn't know yet (a newer server's key, such as areas.orders, or value) is carried
// through unchanged, never dropped or replaced: a save sends it back as it came.
//
// Maps rather than objects for what the server names: a code called "constructor" is not one.

export const isObject = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
export const words = (value) => (typeof value === "string" ? value.trim() : "");
const capitalized = (value) => {
  const text = words(value);
  return text ? `${text[0].toUpperCase()}${text.slice(1)}` : "";
};
const missing = (value) => value === null || value === undefined;

const DOT = ` ${String.fromCharCode(0xb7)} `;
const ARROW = String.fromCharCode(0x2192);

// The areas, in the spec's order. `short` is the area's name in a one-line summary; `note`, a line
// the grid shows under the area's name.
export const AREAS = [
  {
    key: "charts",
    label: "Charts & schedule",
    short: "Charts",
    note: "Agents only read charts today, so Read-only and Full do the same.",
  },
  { key: "fax", label: "Fax inbox", short: "Fax inbox" },
  { key: "tasks", label: "Tasks & messages", short: "Tasks" },
  { key: "outreach", label: "Patient outreach", short: "Outreach" },
];
export const SENDS = [
  { key: "sms", label: "Texts" },
  { key: "email", label: "Emails" },
  { key: "fax", label: "Faxes" },
];
export const AREA_VALUES = [
  { value: "off", label: "Off" },
  { value: "read", label: "Read-only" },
  { value: "full", label: "Full" },
];
export const SEND_VALUES = [
  { value: "off", label: "Off" },
  { value: "ask", label: "Ask first" },
  { value: "allowed", label: "Allowed" },
];
export const AUTO_VALUES = [
  { value: true, label: "On" },
  { value: false, label: "Off" },
];

// When a clinic has stored none: today's behaviour.
export const DEFAULTS = Object.freeze({
  areas: Object.freeze({ charts: "full", fax: "full", tasks: "full", outreach: "full" }),
  sends: Object.freeze({ sms: "allowed", email: "allowed", fax: "allowed" }),
  auto: true,
});

// ---- settings ------------------------------------------------------------------------------------

const GROUPS = ["areas", "sends", "auto"];

// What `group` sets: the spec's keys in the spec's order, then any other key it holds, sorted (so
// two copies compare the same). A null value is no value. Null when nothing is set.
function setIn(group, keys) {
  if (!isObject(group)) return null;
  const out = {};
  keys.forEach(({ key }) => {
    if (!missing(group[key])) out[key] = group[key];
  });
  Object.keys(group).sort().forEach((key) => {
    if (!(key in out) && !missing(group[key])) out[key] = group[key];
  });
  return Object.keys(out).length ? out : null;
}

// What `settings` sets, and nothing more: empty values and groups are dropped; everything else,
// known or not, is kept. A person's override as the grid edits it.
export function partialOf(settings) {
  const out = {};
  if (!isObject(settings)) return out;
  const areas = setIn(settings.areas, AREAS);
  if (areas) out.areas = areas;
  const sends = setIn(settings.sends, SENDS);
  if (sends) out.sends = sends;
  if (!missing(settings.auto)) out.auto = settings.auto;
  Object.keys(settings).sort().forEach((key) => {
    if (!GROUPS.includes(key) && !missing(settings[key])) out[key] = settings[key];
  });
  return out;
}

// Every key set: what `settings` sets, the defaults for what it doesn't. A fresh object each time.
export function complete(settings) {
  const { areas, sends, auto, ...rest } = partialOf(settings);
  return {
    areas: { ...DEFAULTS.areas, ...areas },
    sends: { ...DEFAULTS.sends, ...sends },
    auto: auto === undefined ? DEFAULTS.auto : auto,
    ...rest,
  };
}

// Whether an override sets anything (else the person follows the clinic default in everything).
export const isCustomized = (override) => Object.keys(partialOf(override)).length > 0;

// Whether two settings set the same keys to the same values, whatever their order.
export const sameSettings = (a, b) => JSON.stringify(partialOf(a)) === JSON.stringify(partialOf(b));

// ---- labels ----------------------------------------------------------------------------------------

// "areas.charts" -> "Charts & schedule"; a key nobody knows is shown as it is.
export function keyLabel(key) {
  if (key === "auto") return "Auto mode";
  const [group, name, ...rest] = String(key).split(".");
  const list = group === "areas" ? AREAS : group === "sends" ? SENDS : [];
  const found = rest.length ? null : list.find((item) => item.key === name);
  return found ? found.label : String(key);
}

export function valueLabel(key, value) {
  const list = key === "auto"
    ? AUTO_VALUES
    : String(key).startsWith("areas.") ? AREA_VALUES : String(key).startsWith("sends.") ? SEND_VALUES : [];
  const found = list.find((item) => item.value === value);
  return found ? found.label : String(value);
}

const TYPES = new Map([
  ["general", "General"],
  ["orchestrator", "Orchestrator"],
  ["fax", "Fax"],
  ["charts", "Charts & schedule"],
  ["tasks", "Tasks & messages"],
  ["outreach", "Outreach"],
  ["texts", "Patient texts"],
  ["messages", "Team messages"],
  ["forms", "Forms"],
  ["watcher", "Watcher"],
]);
export const typeLabel = (type) => TYPES.get(type) || words(type);
export const roleLabel = capitalized;
export const statusLabel = capitalized;

const MODES = new Map([["ask", "Ask"], ["auto", "Auto"]]);
const modeLabel = (mode) => MODES.get(mode) || words(mode);

// An agent's mode, and how it runs when the settings change that: "Auto -> runs as Ask".
export function modeWords(mode, effectiveMode) {
  const stored = modeLabel(mode);
  if (!effectiveMode || effectiveMode === mode) return stored;
  return `${stored} ${ARROW} runs as ${modeLabel(effectiveMode)}`;
}

// ---- one-line summaries ------------------------------------------------------------------------------

const AREA_WORDS = new Map([["read", "read-only"], ["off", "off"]]);
const SEND_WORDS = new Map([["ask", "ask first"], ["off", "off"]]);

// A value this page doesn't know, named as it is: a string as written, anything else as JSON.
const unknownWord = (value) => words(value) || JSON.stringify(value);

// The word for a value that narrows, or null when it narrows nothing (the widest value). A value
// this page doesn't know is named, never read as the widest.
const narrowing = (value, widest, known) => {
  if (value === widest) return null;
  return known.get(value) ?? unknownWord(value);
};

// What `settings` narrows, in a line: "Charts read-only . Texts off . Ask only"; "Full access" when
// it narrows nothing. `withAuto: false` leaves Auto out (an agent's mode already says it).
export function summary(settings, { withAuto = true } = {}) {
  if (!isObject(settings)) return "";
  const full = complete(settings);
  const parts = [];
  AREAS.forEach(({ key, short }) => {
    const word = narrowing(full.areas[key], "full", AREA_WORDS);
    if (word) parts.push(`${short} ${word}`);
  });
  SENDS.forEach(({ key, label }) => {
    const word = narrowing(full.sends[key], "allowed", SEND_WORDS);
    if (word) parts.push(`${label} ${word}`);
  });
  if (withAuto && full.auto !== true) parts.push(full.auto === false ? "Ask only" : `Auto ${unknownWord(full.auto)}`);
  return parts.length ? parts.join(DOT) : "Full access";
}

// ---- history -------------------------------------------------------------------------------------

// A log entry ({at, editor_name, target: "defaults" | "<name>", cleared, changes: [{key, from, to}]})
// as a sentence. Only an override that was cleared (`cleared: true`) reads as a reset; a key put back
// to the clinic default (to: null) is said key by key.
export function historySentence(entry) {
  const who = words(entry?.editor_name) || "Someone";
  const toDefaults = entry?.target === "defaults";
  const name = words(entry?.target) || "someone";
  const whose = toDefaults ? "the clinic defaults" : `${name}'s settings`;
  if (!toDefaults && entry?.cleared === true) return `${who} reset ${name}'s settings to the clinic default.`;
  const changes = Array.isArray(entry?.changes) ? entry.changes.filter(isObject) : [];
  if (!changes.length) return `${who} saved ${whose} with no changes.`;
  const parts = changes.map(({ key, from, to }) => {
    const label = keyLabel(key);
    const after = missing(to) ? (toDefaults ? "not set" : "the clinic default") : valueLabel(key, to);
    if (missing(from)) return toDefaults ? `${label} to ${after}` : `${label} from the clinic default to ${after}`;
    return `${label} from ${valueLabel(key, from)} to ${after}`;
  });
  return `${who} changed ${whose}: ${parts.join("; ")}.`;
}

// A log entry's time, in the viewer's own words; "" for a time that can't be read.
export function whenText(at) {
  if (typeof at !== "string" && typeof at !== "number") return "";
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

// ---- refusals ------------------------------------------------------------------------------------

// Why a person's row can't be opened: the server's own `lock_reason` when it sends one; else the
// reason the server refuses a superadmin's override to everyone but a superadmin, or a plain one.
export function lockReason(user) {
  const own = words(user?.lock_reason);
  if (own) return own;
  return user?.role === "superadmin"
    ? "Only a superadmin can change a superadmin's agent settings."
    : "You can't change this person's agent settings.";
}

// The server answers {"error": "<sentence>"}, shown as it comes; a few answers are codes instead,
// each with its own sentence here.
const CODES = new Map([
  ["unauthenticated", "Your session has expired. Reload the page, and sign in again if that does not fix it."],
  ["forbidden", "You can't manage agent permissions here."],
  ["view_as_session_is_read_only", "This is a view-as session, so nothing can be changed."],
  ["not found", "That person can't be found. They may have left the clinic."],
  ["invalid JSON", "Something went wrong."],
]);
const GENERIC = "Something went wrong.";

export function policyErrorText(err) {
  const message = typeof err === "string" ? err : err?.message;
  if (typeof message !== "string" || message.trim() === "") return GENERIC;
  return CODES.get(message) ?? message;
}
